import { useState, useEffect, useRef, useCallback } from 'react'

export interface UseCameraOptions {
  deviceId?: string
  width?: number
  height?: number
  frameRate?: number
  audioDeviceId?: string
}

export function useCamera(options: UseCameraOptions = {}) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const captureVideoRef = useRef<HTMLVideoElement | null>(null)
  const streamRef = useRef<MediaStream | null>(null)
  /** Bumped by every start()/stop(), so a start that resolves late knows it was superseded. */
  const generationRef = useRef(0)
  const recorderRef = useRef<MediaRecorder | null>(null)
  const chunksRef = useRef<Blob[]>([])
  const [error, setError] = useState<string | null>(null)
  const [ready, setReady] = useState(false)
  const [devices, setDevices] = useState<MediaDeviceInfo[]>([])
  const [isWaitingForPermission, setIsWaitingForPermission] = useState(false)
  /** The camera is opening (e.g. right after the screensaver). Not a permission problem. */
  const [isStarting, setIsStarting] = useState(false)
  /** Once the camera has opened on this page, permission is settled; later starts are just slow hardware. */
  const everStartedRef = useRef(false)

  // Ensure we have a hidden video element for capturing that stays mounted
  useEffect(() => {
    const video = document.createElement('video')
    video.autoplay = true
    video.playsInline = true
    video.muted = true
    // Some browsers don't render/play video if display is none
    video.style.position = 'fixed'
    video.style.top = '0'
    video.style.left = '0'
    video.style.width = `${options.width || 1920}px`
    video.style.height = `${options.height || 1080}px`
    video.style.opacity = '0'
    video.style.pointerEvents = 'none'
    document.body.appendChild(video)
    captureVideoRef.current = video
    return () => {
      document.body.removeChild(video)
    }
  }, [options.width, options.height])

  const enumerate = useCallback(async () => {
    try {
      const devs = await navigator.mediaDevices.enumerateDevices()
      setDevices(devs.filter((d) => d.kind === 'videoinput'))
    } catch {
      setDevices([])
    }
  }, [])

  const attach = useCallback((videoEl?: HTMLVideoElement | null) => {
    const target = videoEl ?? videoRef.current
    if (!target || !streamRef.current) return
    target.srcObject = streamRef.current
    target.onloadedmetadata = () => setReady(true)
    if (target.readyState >= 1) setReady(true)
  }, [])

  const start = useCallback(async () => {
    setError(null)
    const generation = ++generationRef.current
    // Only this start may raise the permission notice: a start that was
    // superseded (e.g. by the screensaver stopping the camera) must not leave
    // "Permissions Required" on screen with nothing left to clear it.
    const current = () => generation === generationRef.current
    setIsStarting(true)
    // The permission notice is only meaningful the first time, in a normal
    // browser. The kiosk app grants camera access itself, and after a first
    // successful start a slow open is just the camera waking up (e.g. after
    // the screensaver), which the "Starting camera" indicator covers.
    const mayAskPermission = !everStartedRef.current && !window.electronAPI?.isElectron?.()
    const timer = setTimeout(() => { if (current() && mayAskPermission) setIsWaitingForPermission(true) }, 2000)

    if (mayAskPermission && navigator.permissions && navigator.permissions.query) {
      navigator.permissions.query({ name: 'camera' as PermissionName }).then(status => {
        if (status.state === 'prompt' && current()) {
          setIsWaitingForPermission(true)
        }
      }).catch(() => {})
    }
    try {
      if (streamRef.current) {
        streamRef.current.getTracks().forEach((t) => t.stop())
      }
      const constraints: MediaStreamConstraints = {
        video: {
          deviceId: options.deviceId ? { exact: options.deviceId } : undefined,
          width: { ideal: options.width || 1920 },
          height: { ideal: options.height || 1080 },
          frameRate: { ideal: options.frameRate || 30 },
        },
        audio: options.audioDeviceId ? { deviceId: { exact: options.audioDeviceId } } : true,
      }
      const stream = await navigator.mediaDevices.getUserMedia(constraints)
      // stop() (or a newer start()) ran while the camera was still opening.
      // Keeping this stream would switch the camera back on after it was
      // paused, e.g. when the screensaver kicks in right after a start.
      if (!current()) {
        stream.getTracks().forEach((t) => t.stop())
        clearTimeout(timer)
        return
      }
      clearTimeout(timer)
      everStartedRef.current = true
      setIsWaitingForPermission(false)
      setIsStarting(false)
      streamRef.current = stream
      
      // Always attach to our hidden capture video
      if (captureVideoRef.current) {
        captureVideoRef.current.srcObject = stream
        captureVideoRef.current.play().catch(() => {})
      }
      
      attach()
      await enumerate()
    } catch (err) {
      clearTimeout(timer)
      if (!current()) return
      setIsWaitingForPermission(false)
      setIsStarting(false)
      setError(err instanceof Error ? err.message : 'Camera failed')
    }
  }, [options.deviceId, options.width, options.height, options.frameRate, options.audioDeviceId, enumerate, attach])

  const stop = useCallback(() => {
    generationRef.current++
    streamRef.current?.getTracks().forEach((t) => t.stop())
    streamRef.current = null
    if (captureVideoRef.current) captureVideoRef.current.srcObject = null
    setReady(false)
    setIsWaitingForPermission(false)
    setIsStarting(false)
  }, [])

  const capture = useCallback((): string | null => {
    // Prefer the persistent capture video element
    const video = captureVideoRef.current || videoRef.current
    if (!video || video.readyState < 2 || video.videoWidth === 0) return null
    
    const canvas = document.createElement('canvas')
    canvas.width = video.videoWidth
    canvas.height = video.videoHeight
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    
    ctx.drawImage(video, 0, 0)
    
    // Safety check: ensure the frame isn't just pure black
    // Some cameras/browsers report readyState 2 but still output black for a few frames
    const pixels = ctx.getImageData(0, 0, canvas.width, canvas.height).data
    let isBlack = true
    // Sample a few pixels to see if any have color
    for (let i = 0; i < pixels.length; i += 1024) {
      if (pixels[i] > 10 || pixels[i+1] > 10 || pixels[i+2] > 10) {
        isBlack = false
        break
      }
    }
    
    if (isBlack) return null

    return canvas.toDataURL('image/jpeg', 0.92)
  }, [])
 
  const startRecording = useCallback(() => {
    if (!streamRef.current) return
    if (recorderRef.current && recorderRef.current.state !== 'inactive') {
      console.warn('Recording already in progress')
      return
    }
    chunksRef.current = []
    
    let options = { mimeType: 'video/webm;codecs=vp8' }
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      console.warn('vp8 not supported, trying default webm')
      options = { mimeType: 'video/webm' }
    }
    if (!MediaRecorder.isTypeSupported(options.mimeType)) {
      console.warn('webm not supported, trying mp4')
      options = { mimeType: 'video/mp4' }
    }

    try {
      const recorder = new MediaRecorder(streamRef.current, options)
      recorder.ondataavailable = (e) => {
        if (e.data.size > 0) {
          chunksRef.current.push(e.data)
        }
      }
      recorder.start(1000) // capture in 1s slices to be safer
      recorderRef.current = recorder
      console.log('Started recording with', options.mimeType)
    } catch (err) {
      console.error('Failed to start MediaRecorder', err)
    }
  }, [])
 
  const stopRecording = useCallback(() => {
    return new Promise<void>((resolve) => {
      if (!recorderRef.current || recorderRef.current.state === 'inactive') {
        resolve()
        return
      }
      recorderRef.current.onstop = () => {
        recorderRef.current = null
        resolve()
      }
      recorderRef.current.stop()
    })
  }, [])
 
  const requestVideoBlob = useCallback((): Blob | null => {
    if (chunksRef.current.length === 0) return null
    const type = recorderRef.current?.mimeType || 'video/webm'
    return new Blob(chunksRef.current, { type })
  }, [])

  useEffect(() => {
    enumerate()
    return () => stop()
  }, [enumerate, stop])

  return { videoRef, captureVideoRef, start, stop, capture, attach, ready, error, devices, stream: streamRef.current, startRecording, stopRecording, requestVideoBlob, isWaitingForPermission, isStarting }
}
